import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaSwitch from 'components/SparaSwitch/SparaSwitch';
import CustomInput from 'components/CustomInput/CustomInput';
import EventFormBuilder from 'components/EventFormBuilder/EventFormBuilder';
import SingleImageUpload from 'components/SingleImageUpload/SingleImageUpload';
import { companyUserApi, eventApi, fetcher, groupApi } from 'lib/api';
import styles from './EventForm.module.scss';

const toLocalInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const emptyForm = {
    torgid_special_company_id: '',
    title: '',
    title_en: '',
    description: '',
    description_en: '',
    starts_at: '',
    ends_at: '',
    location: '',
    registration_opens_at: '',
    registration_deadline: '',
    capacity: '',
    waitlist_enabled: false,
    show_remaining_seats: false,
    max_guests_per_registration: 0,
    attendance_policy: 'registered_only',
    image: '',
    visible_in_app: true,
    fields: [],
    scanner_user_ids: [],
};

const EventForm = ({ t }) => {
    const { id } = useParams();
    const isUpdating = !!(id && id !== 'new');
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isGroupAdmin = (user.roles || []).includes('group_admin');
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const { data: groupsData } = useSWR(
        groupApi.list({ 'my-groups': isGroupAdmin ? true : null }),
        fetcher
    );
    const groups = groupsData?.results || [];
    const { data: eventData } = useSWR(isUpdating ? eventApi.getById(id) : null, fetcher);
    const event = eventData?.results;

    const scannerKey = form.torgid_special_company_id
        ? companyUserApi.listByIds([], [form.torgid_special_company_id])
        : null;
    const { data: scannersData } = useSWR(scannerKey, fetcher);
    const scannerUsers = useMemo(() => {
        const rows = scannersData?.results || [];
        const byUser = {};
        rows.forEach((row) => {
            const uid = row.torgid_user_id;
            if (!uid || byUser[uid]) return;
            byUser[uid] = row.torgid_user || { id: uid, name: `#${uid}` };
        });
        return Object.values(byUser);
    }, [scannersData]);

    useEffect(() => {
        if (!event) return;
        setForm({
            torgid_special_company_id: event.torgid_special_company_id || '',
            title: event.title || '',
            title_en: event.title_en || '',
            description: event.description || '',
            description_en: event.description_en || '',
            starts_at: toLocalInput(event.starts_at),
            ends_at: toLocalInput(event.ends_at),
            location: event.location || '',
            registration_opens_at: toLocalInput(event.registration_opens_at),
            registration_deadline: toLocalInput(event.registration_deadline),
            capacity: event.capacity ?? '',
            waitlist_enabled: !!event.waitlist_enabled,
            show_remaining_seats: !!event.show_remaining_seats,
            max_guests_per_registration: event.max_guests_per_registration ?? 0,
            attendance_policy: event.attendance_policy || 'registered_only',
            image: event.image || '',
            visible_in_app: event.visible_in_app !== false,
            fields: event.fields || [],
            scanner_user_ids: event.scanner_user_ids || [],
        });
    }, [event]);

    useEffect(() => {
        if (isUpdating || form.torgid_special_company_id || groups.length !== 1) return;
        setForm((prev) => ({ ...prev, torgid_special_company_id: groups[0].id }));
    }, [groups, form.torgid_special_company_id, isUpdating]);

    const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const toggleScanner = (userId) => {
        setForm((prev) => {
            const ids = prev.scanner_user_ids.map(Number);
            const next = ids.includes(userId)
                ? ids.filter((id) => id !== userId)
                : [...ids, userId];
            return { ...prev, scanner_user_ids: next };
        });
    };

    const handleSave = async () => {
        setError('');
        if (!form.title.trim()) {
            setError(t('events.validation_title'));
            return;
        }
        if (!form.torgid_special_company_id) {
            setError(t('events.validation_group'));
            return;
        }
        if (!isUpdating && !form.image) {
            setError(t('events.validation_image'));
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            torgid_special_company_id: Number(form.torgid_special_company_id),
            capacity: form.capacity === '' ? null : Number(form.capacity),
            max_guests_per_registration: Number(form.max_guests_per_registration || 0),
            starts_at: form.starts_at || null,
            ends_at: form.ends_at || null,
            registration_opens_at: form.registration_opens_at || null,
            registration_deadline: form.registration_deadline || null,
        };
        try {
            if (isUpdating) {
                await fetcher(eventApi.update(id), 'PUT', payload);
            } else {
                await fetcher(eventApi.create(), 'POST', payload);
            }
            navigate('/events');
        } catch (err) {
            setError(err?.response?.data?.message || t('events.save_failed'));
        } finally {
            setSaving(false);
        }
    };

    const hasResponses = (event?.stats?.registered_count || 0) + (event?.stats?.waitlisted_count || 0) > 0;

    return (
        <div className={styles.page}>
            <SparaTitle title={isUpdating ? t('events.edit') : t('events.new')} />
            {error ? <p className={styles.error}>{error}</p> : null}
            <div className={styles.card}>
                <FormControl fullWidth>
                    <InputLabel shrink>{t('events.group')}</InputLabel>
                    <Select
                        value={form.torgid_special_company_id}
                        disabled={isUpdating}
                        onChange={(e) => patch('torgid_special_company_id', e.target.value)}
                    >
                        {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <SparaSwitch
                    label={t('events.visible_in_app')}
                    checked={!!form.visible_in_app}
                    onChange={(e) => patch('visible_in_app', e.target.checked)}
                />
                <div className={styles.imageRow}>
                    <div>
                        <p className={styles.label}>{t('events.image')}</p>
                        <SingleImageUpload
                            image={form.image}
                            aspectRatio="16/9"
                            onUpload={(data) => patch('image', data)}
                            onDelete={() => patch('image', isUpdating ? null : '')}
                            onError={() => patch('image', isUpdating ? null : '')}
                            disabledDeleteBtn={!isUpdating}
                        />
                    </div>
                    <div className={styles.preview}>
                        <p className={styles.label}>{t('events.preview')}</p>
                        <div className={styles.previewCard}>
                            <div className={styles.previewImage}>
                                {form.image ? (
                                    <img src={form.image} alt="" />
                                ) : (
                                    <div className={styles.previewPlaceholder} />
                                )}
                            </div>
                            <p className={styles.previewTitle}>
                                {form.title || t('events.title')}
                            </p>
                        </div>
                    </div>
                </div>
                <CustomInput
                    labelText={t('events.title')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: form.title,
                        onChange: (e) => patch('title', e.target.value),
                    }}
                />
                <CustomInput
                    labelText={t('events.title_en')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: form.title_en,
                        onChange: (e) => patch('title_en', e.target.value),
                    }}
                />
                <CustomInput
                    labelText={t('events.description')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        multiline: true,
                        rows: 3,
                        value: form.description,
                        onChange: (e) => patch('description', e.target.value),
                    }}
                />
                <label className={styles.label}>
                    {t('events.starts_at')}
                    <input
                        type="datetime-local"
                        className={styles.dateInput}
                        value={form.starts_at}
                        onChange={(e) => patch('starts_at', e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    {t('events.ends_at')}
                    <input
                        type="datetime-local"
                        className={styles.dateInput}
                        value={form.ends_at}
                        onChange={(e) => patch('ends_at', e.target.value)}
                    />
                </label>
                <CustomInput
                    labelText={t('events.location')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        value: form.location,
                        onChange: (e) => patch('location', e.target.value),
                    }}
                />
                <label className={styles.label}>
                    {t('events.registration_opens_at')}
                    <input
                        type="datetime-local"
                        className={styles.dateInput}
                        value={form.registration_opens_at}
                        onChange={(e) => patch('registration_opens_at', e.target.value)}
                    />
                </label>
                <label className={styles.label}>
                    {t('events.registration_deadline')}
                    <input
                        type="datetime-local"
                        className={styles.dateInput}
                        value={form.registration_deadline}
                        onChange={(e) => patch('registration_deadline', e.target.value)}
                    />
                </label>
                <CustomInput
                    labelText={t('events.capacity')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        type: 'number',
                        value: form.capacity,
                        onChange: (e) => patch('capacity', e.target.value),
                    }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.show_remaining_seats}
                            onChange={(e) => patch('show_remaining_seats', e.target.checked)}
                        />
                    }
                    label={t('events.show_remaining_seats')}
                />
                <SparaSwitch
                    label={t('events.waitlist')}
                    checked={form.waitlist_enabled}
                    onChange={(e) => patch('waitlist_enabled', e.target.checked)}
                />
                <CustomInput
                    labelText={t('events.max_guests')}
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                        type: 'number',
                        value: form.max_guests_per_registration,
                        onChange: (e) => patch('max_guests_per_registration', e.target.value),
                    }}
                />
                <FormControl fullWidth>
                    <InputLabel shrink>{t('events.attendance_policy')}</InputLabel>
                    <Select
                        value={form.attendance_policy}
                        onChange={(e) => patch('attendance_policy', e.target.value)}
                    >
                        <MenuItem value="registered_only">{t('events.policy_registered_only')}</MenuItem>
                        <MenuItem value="any_member">{t('events.policy_any_member')}</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <div className={styles.card}>
                <EventFormBuilder
                    fields={form.fields}
                    hasResponses={hasResponses}
                    onChange={(fields) => patch('fields', fields)}
                />
            </div>

            <div className={styles.card}>
                <h4 className={styles.sectionTitle}>{t('events.scanners')}</h4>
                <p className={styles.help}>{t('events.scanners_help')}</p>
                {scannerUsers.length === 0 ? (
                    <p className={styles.help}>{t('events.scanners_empty')}</p>
                ) : (
                    scannerUsers.map((scanner) => (
                        <FormControlLabel
                            key={scanner.id}
                            control={
                                <Checkbox
                                    checked={form.scanner_user_ids.map(Number).includes(Number(scanner.id))}
                                    onChange={() => toggleScanner(Number(scanner.id))}
                                />
                            }
                            label={scanner.name || scanner.email || `#${scanner.id}`}
                        />
                    ))
                )}
            </div>

            <div className={styles.actions}>
                <SparaButton variant="outlined" onClick={() => navigate('/events')}>
                    {t('close')}
                </SparaButton>
                <SparaButton onClick={handleSave} loading={saving}>
                    {t('save')}
                </SparaButton>
            </div>
        </div>
    );
};

export default withTranslation()(EventForm);
